import React, { useState } from 'react';
import axios from 'axios';
import Popup from 'reactjs-popup';
import PledgeKpis              from './PledgeKpis';
import InstallmentScheduleTable from './InstallmentScheduleTable';
import PledgeDetailsSidebar    from './PledgeDetailsSidebar';
import ScheduleControls        from './ScheduleControls';
import PledgeFormModal         from '../../shared/components/pledges/PledgeFormModal';
import formatPledgeAmount      from '../../shared/utils/formatPledgeAmount';
import formatLongDate          from '../../shared/utils/formatLongDate';
import { toastError, toastSuccess } from '../../../shared/toastHelper';
import GivingConfirmModal      from '../../shared/components/GivingConfirmModal';

const STATUS_LABELS = {
  active: 'Active', partially_paid: 'Partially Paid',
  delinquent: 'Delinquent', fulfilled: 'Fulfilled', written_off: 'Written Off',
};
const STATUS_BADGE_CLASSES = {
  active: 'pd-b-brand',
  partially_paid: 'pd-b-warning',
  delinquent: 'pd-b-error',
  fulfilled: 'pd-b-success',
  written_off: 'pd-b-gray',
};

const FREQ_LABELS = {
  one_time: 'One-time',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annually: 'Semi-Annually',
  annually: 'Annually',
};

const fmt = (cents) => formatPledgeAmount(cents);

const ComingSoon = ({ label, icon }) => (
  <button className="pd-btn pd-btn-secondary" disabled title="Coming soon">
    <i className={`fal ${icon}`} />
    {label}
  </button>
);

export default function PledgeDetailPage({ team, pledge: initialPledge }) {
  const [pledge, setPledge] = useState(initialPledge);
  const [editing, setEditing] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [resolving, setResolving] = useState(null);

  const reload = async () => {
    try {
      const { data } = await axios.get(`/teams/${team.id}/crm/pledges/${pledge.id}.json`);
      setPledge(data);
    } catch (e) {
      toastError('Could not reload pledge. Refresh the page.');
    }
  };

  const applyInstallment = (updated) => {
    setPledge((current) => ({
      ...current,
      installments: current.installments.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)),
    }));
  };

  const retryInstallment = async (installment) => {
    setRetryingId(installment.id);
    try {
      const { data } = await axios.post(`/teams/${team.id}/crm/pledges/${pledge.id}/installments/${installment.id}/retry_charge.json`);
      applyInstallment(data);
      toastSuccess('Retrying the payment now.');
      await reload();
    } catch (e) {
      toastError(e?.response?.data?.error || 'Could not retry the payment. Try again.');
    } finally {
      setRetryingId(null);
    }
  };

  const resolveInstallment = async () => {
    const installment = resolving;
    setRetryingId(installment.id);
    try {
      const { data } = await axios.post(`/teams/${team.id}/crm/pledges/${pledge.id}/installments/${installment.id}/resolve.json`);
      applyInstallment(data);
      toastSuccess(`Installment ${installment.sequence} marked resolved.`);
      setResolving(null);
      await reload();
    } catch (e) {
      toastError(e?.response?.data?.error || 'Could not mark the payment resolved. Try again.');
    } finally {
      setRetryingId(null);
    }
  };

  // Lets PledgeFormModal handle 422/409; only re-throws so the modal's catch fires.
  const previewSchedule = async (attrs) => {
    const { data } = await axios.post(
      `/teams/${team.id}/crm/pledges/preview_schedule.json`, { pledge: attrs }
    );
    return data;
  };

  const updatePledge = async (id, attrs) => {
    const { data } = await axios.patch(
      `/teams/${team.id}/crm/pledges/${id}.json`, { pledge: attrs }
    );
    setPledge(data);
    return data;
  };

  // The donor is a fan OR an organization; link back to whichever detail page.
  const donorUrl = pledge.organization_id
    ? `/teams/${team.id}/crm/organizations/${pledge.organization_id}`
    : `/teams/${team.id}/crm/fans/${pledge.fan_id}`;

  const doCancelPledge = async () => {
    try {
      await axios.delete(`/teams/${team.id}/crm/pledges/${pledge.id}.json`);
      window.location.href = donorUrl;
    } catch (e) {
      toastError('Could not cancel pledge. Refresh and try again.');
    }
  };

  const pledgesIndexUrl = `${donorUrl}#pledges`;
  const metaLine = [
    pledge.giving_campaign?.name ? `${pledge.giving_campaign.name} campaign` : pledge.fund?.name,
    <React.Fragment key="freq"><strong>{FREQ_LABELS[pledge.installment_frequency]}</strong></React.Fragment>,
    `${fmt(pledge.total_amount_cents)} over ${pledge.installments_count} installments`,
    `Created ${formatLongDate(pledge.pledge_date)}`,
  ];

  return (
    <div className="pledge-detail-page">
      <div className="pd-screen">
        <header className="pd-head">
          <div>
            <nav className="pd-crumbs">
              <a href={pledgesIndexUrl}>Pledges</a>
              <span className="pd-crumbs-sep">/</span>
              <span className="pd-crumbs-cur">{pledge.display_number}</span>
            </nav>
            <div className="pd-title-row">
              <h1>{pledge.name}</h1>
              <span className={`pd-badge ${STATUS_BADGE_CLASSES[pledge.status] || 'pd-b-gray'}`}>
                <span className="pd-badge-dot" />
                {STATUS_LABELS[pledge.status]}
              </span>
            </div>
            <p className="pd-head-meta">
              {metaLine.filter(Boolean).map((piece, i) => (
                <React.Fragment key={i}>
                  {i > 0 && ' · '}
                  {piece}
                </React.Fragment>
              ))}
            </p>
          </div>
          <div className="pd-head-actions">
            <button className="pd-btn pd-btn-secondary" onClick={() => setEditing(true)}>
              <i className="fal fa-pen" />Edit
            </button>
            <ComingSoon label="Send reminder" icon="fa-paper-plane" />
            <ComingSoon label="Apply payment" icon="fa-plus" />
            <Popup
              open={actionsOpen}
              onOpen={() => setActionsOpen(true)}
              onClose={() => setActionsOpen(false)}
              trigger={<button className="pd-btn-icon" aria-label="More actions"><i className="fal fa-ellipsis-v" /></button>}
              position="bottom right"
              arrow={false}
              closeOnDocumentClick
              contentStyle={{
                border: '1px solid var(--sl-border-secondary)', borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                padding: '6px', width: '200px',
              }}
            >
              <button
                className="dropdown-item text-danger"
                style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'transparent', textAlign: 'left', borderRadius: '4px', cursor: 'pointer' }}
                onClick={() => { setActionsOpen(false); setCancelConfirmOpen(true); }}
              >
                Cancel pledge
              </button>
            </Popup>
          </div>
        </header>

        <PledgeKpis pledge={pledge} />

        <div className="pd-body-grid">
          <div className="pd-main-col">
            <div className="pd-card">
              <div className="pd-card-head pd-card-head-flush">
                <div>
                  <h2 className="pd-card-title">Burn-down</h2>
                  <div className="pd-card-cap">Remaining balance vs. on-track pace</div>
                </div>
              </div>
              <div className="pd-chart-empty">
                <div className="pd-chart-grid" />
                <div className="pd-empty">
                  <span className="pd-empty-ico"><i className="fal fa-chart-line" /></span>
                  <div className="pd-empty-title">No payment history yet</div>
                  <div className="pd-empty-sub">Your burn-down chart appears here once the first payment is recorded.</div>
                </div>
              </div>
            </div>

            <div className="pd-card">
              <div className="pd-card-head">
                <div>
                  <h2 className="pd-card-title">Installment schedule</h2>
                  <div className="pd-card-cap">
                    {pledge.installments_count} installments · {FREQ_LABELS[pledge.installment_frequency]}
                  </div>
                </div>
                <ScheduleControls teamId={team.id} pledge={pledge} onUpdated={setPledge} />
              </div>
              <div className="pd-card-body-tight">
                <InstallmentScheduleTable installments={pledge.installments} onRetry={pledge.card && !pledge.autopay_paused_at && !pledge.schedule_canceled_at ? retryInstallment : null} onResolve={setResolving} retryingId={retryingId} />
              </div>
            </div>

            <div className="pd-card">
              <div className="pd-card-head pd-card-head-flush">
                <h2 className="pd-card-title">Payments</h2>
              </div>
              <div className="pd-empty">
                <span className="pd-empty-ico"><i className="fal fa-inbox" /></span>
                <div className="pd-empty-title">No payments recorded</div>
                <div className="pd-empty-sub">Apply a payment to start tracking progress against this pledge.</div>
              </div>
            </div>
          </div>

          <aside className="pd-rail">
            <PledgeDetailsSidebar pledge={pledge} />

            <div className="pd-card">
              <div className="pd-card-head pd-card-head-flush">
                <h2 className="pd-card-title">Reminders</h2>
              </div>
              <div className="pd-empty">
                <span className="pd-empty-ico"><i className="fal fa-bell" /></span>
                <div className="pd-empty-title">No reminders configured</div>
                <div className="pd-empty-sub">Add automatic email or SMS reminders before each due date.</div>
              </div>
            </div>

            <div className="pd-card">
              <div className="pd-card-head pd-card-head-flush">
                <h2 className="pd-card-title">Activity</h2>
              </div>
              <div className="pd-empty">
                <span className="pd-empty-ico"><i className="fal fa-clock" /></span>
                <div className="pd-empty-title">No activity yet</div>
                <div className="pd-empty-sub">Edits and payments on this pledge will show up here.</div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {editing && (
        <PledgeFormModal
          mode="edit"
          team={team}
          fan={pledge.organization_id ? null : (pledge.fan || { id: pledge.fan_id })}
          organization={pledge.organization}
          pledge={pledge}
          previewSchedule={previewSchedule}
          updatePledge={updatePledge}
          onClose={() => { setEditing(false); reload(); }}
          onSaved={() => { setEditing(false); reload(); }}
        />
      )}

      <GivingConfirmModal
        open={resolving != null}
        title={resolving ? `Mark installment ${resolving.sequence} resolved?` : ''}
        body={resolving ? `Opendate won't charge this ${fmt(resolving.charged_amount_cents || resolving.amount_cents)} payment again. Use this when the donor paid another way, and log that gift on the pledge so the balance stays right.` : ''}
        confirmLabel="Mark resolved"
        pendingLabel="Marking resolved…"
        pending={retryingId != null}
        onConfirm={resolveInstallment}
        onCancel={() => setResolving(null)}
      />

      <Popup
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        modal
        closeOnDocumentClick
        contentStyle={{
          border: '1px solid var(--sl-border-secondary)',
          borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          width: '420px',
          padding: '24px',
        }}
      >
        {(close) => (
          <div className="pd-confirm-modal">
            <h2>Cancel pledge?</h2>
            <p>This pledge will be marked cancelled. This action cannot be undone.</p>
            <div className="pd-confirm-actions">
              <button className="pd-btn pd-btn-secondary" onClick={close}>Keep pledge</button>
              <button
                className="btn btn-danger"
                onClick={() => { close(); doCancelPledge(); }}
              >
                Cancel pledge
              </button>
            </div>
          </div>
        )}
      </Popup>
    </div>
  );
}
